/* GrowthOS — Account form sections (used by addAccountsForm.jsx for Create/Edit
   Account). Same pattern as leadForm.jsx/contactForm.jsx: section cards in a 2-col
   grid + a full-width notes card, config-driven `SECTIONS`, reusing the exact
   gos-cl-* / gos-dd-* classes — just the field groups match Account's real schema
   (left column: Account Information + Web Presence stacked below it; right column:
   Ownership & Details + Address Information; Notes full-width below) instead of
   Lead's/Contact's. Employees/Annual Revenue/Number of Beds live in "Ownership &
   Details" rather than "Account Information" purely to balance the two columns'
   height (Web Presence being a separate card under Account Info, per design,
   otherwise left ran taller — verified against real row/header pixel heights).
   Keeps the exact handler contract from addAccountsForm (handleChangeForm /
   handleChange / errormsg / refs). "Parent Account" is the one searchable field
   (real accounts list can be large), backed by the existing PaginatedDropdown hook,
   same as Contact's "Account Name". "Number of Beds" only renders when Industry is
   Healthcare, same conditional the legacy Bootstrap form had. */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveOwners, getGenericMasterFilteredListByKey } from '../../redux/selector';
import { NameFormatter } from '../../shared/controls/formatters';
import PaginatedDropdown from '../../shared/component/PaginatedDropdown';
import apiServices from '../../services/service';
import { accountFormSections } from '../../shared/constants';
import { FIc } from "../../shared/component/GosIcon";


/* GrowthOS select (reuses .gos-dd-* dropdown styles); `searchable` adds a search box
   that calls onSearch(text) and an infinite-scroll list that calls onScroll() near
   the bottom — both map straight onto PaginatedDropdown's handleSearch/handleScroll. */
const Select = ({ value, placeholder, options, onChange, disabled, searchable, onSearch, onScroll, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const onListScroll = (e) => {
    if (!onScroll) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) onScroll();
  };
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (value ? ' has-val' : '')} onClick={() => !disabled && setOpen((o) => !o)} disabled={disabled}>
        <span className="gos-dd-label">{value || placeholder || 'Select…'}</span>
        <FIc name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open ? (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <FIc name="search" size={14} />
              <input autoFocus placeholder="Search…" onChange={(e) => onSearch && onSearch(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list" onScroll={onScroll ? onListScroll : undefined}>
            {loading && options.length === 0 ? <div className="gos-dd-empty">Loading…</div> : null}
            {!loading && options.length === 0 ? <div className="gos-dd-empty">No options</div> : null}
            {options.map((o) => (
              <button key={o.value} type="button" className={'gos-dd-item' + (o.label === value ? ' on' : '')} onClick={() => { onChange(o); setOpen(false); }}>
                <span className="gos-dd-grow">{o.label}</span>
                {o.label === value ? <FIc name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
            {loading && options.length > 0 ? <div className="gos-dd-empty">Loading more…</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const AccountForm = ({ formData, handleChangeForm, handleChange, errormsg, refs }) => {
  const owner = useSelector(getActiveOwners);
  const industryDropdown = useSelector(getGenericMasterFilteredListByKey('Industry'));
  const accountTypeDropdown = useSelector(getGenericMasterFilteredListByKey('AccountType'));

  const {
    options: parentAccounts,
    isLoading: isAccountLoading,
    handleSearch: handleAccountSearch,
    handleScroll: handleAccountScroll,
  } = PaginatedDropdown({
    fetchFn: apiServices.getAccountsDropdown,
    responseKey: 'accountList',
  });

  const opt = (list, vKey, lKey, fmt) =>
    (list || []).map((o) => ({ value: o[vKey], label: fmt ? fmt(o[lKey]) : o[lKey] }));

  const isHealthcare = formData?.industry === 'Healthcare';

  // runtime dropdown data keyed by the `source` marker in accountFormSections
  const optionsBySource = {
    accountType: opt(accountTypeDropdown, 'id', 'displayName'),
    industry: opt(industryDropdown, 'id', 'displayName'),
    owner: opt(owner, 'userId', 'display_name', NameFormatter),
    parentAccount: (parentAccounts || []).filter((a) => a.id !== formData?.id).map((a) => ({ value: a.id, label: a.name })),
  };
  // per-field conditions (`when` in accountFormSections) → whether the field renders
  const conditionMet = { healthcare: isHealthcare };

  // static section/field config lives in `accountFormSections` (shared/constants); hydrate the runtime bits
  // (dropdown options + search/scroll handlers + conditional fields) here.
  // field config: text | select | select-search | textarea
  const SECTIONS = accountFormSections.map((s) => ({
    ...s,
    fields: s.fields
      .filter((fl) => !fl.when || conditionMet[fl.when])
      .map((fl) => {
        if (!fl.source) return fl;
        const hydrated = { ...fl, options: optionsBySource[fl.source] || [] };
        if (fl.source === 'parentAccount') {
          hydrated.loading = isAccountLoading;
          hydrated.onSearch = handleAccountSearch;
          hydrated.onScroll = handleAccountScroll;
        }
        return hydrated;
      }),
  }));

  const renderField = (fl) => {
    const err = errormsg?.[fl.name];
    let control;
    if (fl.type === 'select' || fl.type === 'select-search') {
      control = (
        <Select
          value={formData?.[fl.display] || ''}
          placeholder={'Select ' + fl.label}
          options={fl.options}
          onChange={(o) => handleChange(o, fl.name)}
          searchable={fl.type === 'select-search'}
          onSearch={fl.onSearch}
          onScroll={fl.onScroll}
          loading={fl.loading}
        />
      );
    } else if (fl.type === 'textarea') {
      control = (
        <textarea
          className={'gos-cl-input gos-cl-textarea' + (err ? ' err' : '')}
          name={fl.name}
          rows={4}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    } else {
      control = (
        <input
          className={'gos-cl-input' + (err ? ' err' : '')}
          name={fl.name}
          placeholder={fl.ph || 'Enter ' + fl.label}
          value={formData?.[fl.name] ?? ''}
          onChange={handleChangeForm}
        />
      );
    }
    return (
      <div className={'gos-cl-field' + (fl.full ? ' gos-cl-field-full' : '')} key={fl.name} ref={fl.ref ? refs?.[fl.ref] : undefined}>
        <label className="gos-cl-label">
          {fl.label}
          {fl.req ? <span className="gos-cl-req">*</span> : null}
        </label>
        {control}
        {err ? <span className="gos-cl-err">{err}</span> : null}
      </div>
    );
  };

  const secCount = (s) => s.fields.filter((fl) => { const v = formData?.[fl.name]; return v !== null && v !== undefined && String(v).trim() !== ''; }).length;

  const section = (s) => {
    const n = secCount(s);
    const total = s.fields.length;
    return (
      <div className="gos-lr-card gos-cl-sec" id={'sec-' + s.id} key={s.id}>
        <div className="gos-cl-sec-head">
          <span className="gos-cl-sec-ic"><FIc name={s.icon} size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="gos-cl-sec-title">{s.title}</div>
            <div className="gos-cl-sec-sub">{s.sub}</div>
          </div>
          <span className={'gos-cl-count' + (n === total && n > 0 ? ' done' : '')}>{n}/{total}</span>
        </div>
        <div className="gos-cl-sec-body">
          {s.fields.map(renderField)}
        </div>
      </div>
    );
  };

  const byId = (id) => SECTIONS.find((s) => s.id === id);

  return (
    <>
      <div className="gos-cl3-grid">
        <div className="gos-cl3-col">{section(byId('account'))}{section(byId('web'))}</div>
        <div className="gos-cl3-col">{section(byId('ownership'))}{section(byId('address'))}</div>
      </div>
      <div style={{ marginTop: 18 }}>{section(byId('notes'))}</div>
    </>
  );
};

export default AccountForm;
